import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { makeAxiosProgress, type UploadProgressFn } from "@/lib/upload-progress";
import type { DocumentRow, FileType } from "@/lib/types";

export type Visibility = "private" | "staff" | "public";

export interface UploadDocumentInput {
  title: string;
  file_type: FileType;
  category: string;
  description?: string;
  visibility: Visibility;
  /** One or many physical files belonging to this resource. The first
   *  becomes the "primary" (mirrored to Document.file for legacy single
   *  -file consumers); the full set lives in DocumentFile rows. */
  files: File[];
  thumbnail?: File | null;
  /** Optional progress reporter. axios fires this several times per
   *  second while the request body uploads. */
  onProgress?: UploadProgressFn;
}

/**
 * Multipart POST /api/v1/documents/. Returns the freshly-created
 * DocumentRow. Multi-file uploads send the same `files` form key once
 * per attachment; the backend creates a DocumentFile row for each. The
 * query cache invalidates every document key on success so the library
 * + dashboard re-fetch immediately.
 */
export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadDocumentInput) => {
      const form = new FormData();
      form.append("title",       input.title);
      form.append("file_type",   input.file_type);
      form.append("category",    input.category);
      form.append("visibility",  input.visibility);
      // Append `files` once per attachment — Django sees these as a
      // list via request.FILES.getlist("files").
      for (const f of input.files) form.append("files", f);
      if (input.description)      form.append("description",     input.description);
      if (input.thumbnail)        form.append("thumbnail",       input.thumbnail);

      // axios picks Content-Type=multipart/form-data automatically when
      // we hand it a FormData; explicit headers would break the boundary.
      const { data } = await api.post<DocumentRow>("/documents/", form, {
        onUploadProgress: makeAxiosProgress(input.onProgress),
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/** PATCH /api/v1/documents/<id>/ — same field set as upload, file
 *  optional (left unchanged when omitted). visibility re-derives the
 *  workflow state server-side via resource_state_from_visibility. */
export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      { id, input }: { id: string; input: Partial<UploadDocumentInput> },
    ) => {
      const form = new FormData();
      if (input.title           != null) form.append("title",           input.title);
      if (input.description     != null) form.append("description",     input.description);
      if (input.file_type       != null) form.append("file_type",       input.file_type);
      if (input.category        != null) form.append("category",        input.category);
      if (input.visibility      != null) form.append("visibility",      input.visibility);
      // PATCH-time multi-file: any new files get appended to the
      // existing DocumentFile set on the backend.
      if (input.files)                   for (const f of input.files) form.append("files", f);
      if (input.thumbnail)               form.append("thumbnail",       input.thumbnail);
      const { data } = await api.patch<DocumentRow>(`/documents/${id}/`, form, {
        onUploadProgress: makeAxiosProgress(input.onProgress),
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/** DELETE /api/v1/documents/<id>/ — returns 204 + invalidates list queries. */
export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/documents/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/** DELETE /api/v1/documents/<doc_id>/files/<file_id>/ — remove one
 *  attachment from a document's file bundle. Detail + list caches
 *  refetch on success so the file disappears from the UI. */
export function useRemoveDocumentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentId, fileId }: { documentId: string; fileId: string }) => {
      await api.delete(`/documents/${documentId}/files/${fileId}/`);
      return { documentId, fileId };
    },
    onSuccess: ({ documentId }) => {
      qc.invalidateQueries({ queryKey: ["documents", "detail", documentId] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}
